import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useProductSettings } from '@workspace/utils-react';
import type { Settings } from '../../AppContainer';
import { useEvoContext } from '../../hooks/useEvoContext';
import { getAllQuartersFrom, formatFiscalYear } from '../../lib/fiscal-year';
import { triggerBatchDatabricks, getBatchJobStatus, getFailedBatchJobs, continueAssessmentJob, continueProjectEvaluationJob, removeBatchJob, forceFailBatchJob } from '../../api/batch-admin';
import { triggerCapexEvaluation, triggerRndEvaluation } from '../../api/project-evaluations';
import type { BatchJobStatusResponse, BatchJobAcceptedResponse, BatchJobSummaryResponse } from '../../api/shared/types';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { formatDateTimeUK } from '../../lib/date-utils';
import { Badge } from '../../components/ui/badge';
import { Spinner } from '../../components/ui/spinner';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/alert';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { AlertCircle, RefreshCw, Trash2, XCircle } from 'lucide-react';

interface ActiveJob {
  jobId: string;
  jobType: string;
  status: BatchJobStatusResponse | null;
  polling: boolean;
}

export function BatchProcessContent() {
  const { apiUrl } = useProductSettings<Settings>();
  const { user } = useEvoContext();

  const [selectedQuarter, setSelectedQuarter] = useState<string>('');
  const [selectedAreaLevel, setSelectedAreaLevel] = useState<string>('');

  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const [triggerLoading, setTriggerLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<string>('run');
  const [failedJobs, setFailedJobs] = useState<BatchJobSummaryResponse[]>([]);
  const [failedJobsLoading, setFailedJobsLoading] = useState(false);
  const [failedJobsError, setFailedJobsError] = useState<string | null>(null);
  const [continueLoading, setContinueLoading] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState<string | null>(null);
  const [forceFailLoading, setForceFailLoading] = useState<string | null>(null);

  const intervalRefs = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  const quarters = useMemo(() => getAllQuartersFrom(2026), []);
  const availableQuarters = quarters;

  useEffect(() => {
    const refs = intervalRefs.current;
    return () => {
      for (const interval of refs.values()) {
        clearInterval(interval);
      }
      refs.clear();
    };
  }, []);

  const startPolling = useCallback((jobId: string) => {
    const interval = setInterval(async () => {
      try {
        const status = await getBatchJobStatus(apiUrl, jobId);
        setActiveJobs((prev) =>
          prev.map((j) =>
            j.jobId === jobId
              ? { ...j, status, polling: status.status !== 'completed' && status.status !== 'failed' }
              : j,
          ),
        );
        if (status.status === 'completed' || status.status === 'failed') {
          const ref = intervalRefs.current.get(jobId);
          if (ref) {
            clearInterval(ref);
            intervalRefs.current.delete(jobId);
          }
        }
      } catch (err) {
        console.warn(`[BatchProcess] Polling error for job ${jobId}:`, err);
        setError(err instanceof Error ? err.message : 'Failed to poll job status');
      }
    }, 60000);
    intervalRefs.current.set(jobId, interval);
  }, [apiUrl]);

  const handleTriggerJob = useCallback(
    async (jobType: 'batch-databricks' | 'capex' | 'rnd') => {
      if (!selectedQuarter) return;
      setError(null);
      setTriggerLoading(jobType);

      try {
        const areaLevel = selectedAreaLevel.trim() === '' ? undefined : selectedAreaLevel.trim();
        let result: BatchJobAcceptedResponse;

        if (jobType === 'batch-databricks') {
          result = await triggerBatchDatabricks(apiUrl, selectedQuarter, areaLevel);
        } else if (jobType === 'capex') {
          result = await triggerCapexEvaluation(apiUrl, selectedQuarter, areaLevel);
        } else {
          result = await triggerRndEvaluation(apiUrl, selectedQuarter, areaLevel);
        }

        const newJob: ActiveJob = {
          jobId: result.job_id,
          jobType,
          status: null,
          polling: true,
        };
        setActiveJobs((prev) => [...prev, newJob]);

        try {
          const initialStatus = await getBatchJobStatus(apiUrl, result.job_id);
          setActiveJobs((prev) =>
            prev.map((j) =>
              j.jobId === result.job_id
                ? { ...j, status: initialStatus, polling: initialStatus.status !== 'completed' && initialStatus.status !== 'failed' }
                : j,
            ),
          );
          if (initialStatus.status !== 'completed' && initialStatus.status !== 'failed') {
            startPolling(result.job_id);
          }
        } catch {
          startPolling(result.job_id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to trigger batch job');
      } finally {
        setTriggerLoading(null);
      }
    },
    [apiUrl, selectedQuarter, selectedAreaLevel, startPolling],
  );

  const fetchFailedJobs = useCallback(async () => {
    setFailedJobsLoading(true);
    setFailedJobsError(null);
    try {
      const jobs = await getFailedBatchJobs(apiUrl);
      setFailedJobs(jobs);
    } catch (err) {
      setFailedJobsError(err instanceof Error ? err.message : 'Failed to fetch failed jobs');
    } finally {
      setFailedJobsLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (activeTab === 'failed') {
      fetchFailedJobs();
    }
  }, [activeTab, fetchFailedJobs]);

  const handleRemoveJob = useCallback(async (job: BatchJobSummaryResponse) => {
    setRemoveLoading(job.job_id);
    setFailedJobsError(null);
    try {
      await removeBatchJob(apiUrl, job.job_id);
      setFailedJobs((prev) => prev.filter((j) => j.job_id !== job.job_id));
    } catch (err) {
      setFailedJobsError(err instanceof Error ? err.message : 'Failed to remove job');
    } finally {
      setRemoveLoading(null);
    }
  }, [apiUrl]);

  const handleContinueJob = useCallback(async (job: BatchJobSummaryResponse) => {
    setContinueLoading(job.job_id);
    setFailedJobsError(null);
    try {
      let result: BatchJobAcceptedResponse;

      if (job.job_type === 'batchassessment') {
        result = await continueAssessmentJob(apiUrl, job.job_id);
      } else {
        result = await continueProjectEvaluationJob(apiUrl, job.job_id);
      }

      const newJob: ActiveJob = {
        jobId: result.job_id,
        jobType: job.job_type ?? 'unknown',
        status: null,
        polling: true,
      };
      setActiveJobs((prev) => [...prev, newJob]);

      try {
        const initialStatus = await getBatchJobStatus(apiUrl, result.job_id);
        setActiveJobs((prev) =>
          prev.map((j) =>
            j.jobId === result.job_id
              ? { ...j, status: initialStatus, polling: initialStatus.status !== 'completed' && initialStatus.status !== 'failed' }
              : j,
          ),
        );
        if (initialStatus.status !== 'completed' && initialStatus.status !== 'failed') {
          startPolling(result.job_id);
        }
      } catch {
        startPolling(result.job_id);
      }

      setActiveTab('run');
    } catch (err) {
      setFailedJobsError(err instanceof Error ? err.message : 'Failed to continue job');
    } finally {
      setContinueLoading(null);
    }
  }, [apiUrl, startPolling]);

  const handleForceFailJob = useCallback(async (job: ActiveJob) => {
    setForceFailLoading(job.jobId);
    setError(null);
    try {
      await forceFailBatchJob(apiUrl, job.jobId);
      const ref = intervalRefs.current.get(job.jobId);
      if (ref) {
        clearInterval(ref);
        intervalRefs.current.delete(job.jobId);
      }
      setActiveJobs((prev) =>
        prev.map((j) =>
          j.jobId === job.jobId
            ? { ...j, polling: false, status: j.status ? { ...j.status, status: 'failed', error_message: 'Job manually force-failed by admin (stuck/not progressing)' } : j.status }
            : j,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to force-fail job');
    } finally {
      setForceFailLoading(null);
    }
  }, [apiUrl]);

  const hasRunningJob = activeJobs.some(
    (j) => j.polling || (j.status && j.status.status !== 'completed' && j.status.status !== 'failed'),
  );

  const getJobTypeLabel = (jobType: string) => {
    switch (jobType) {
      case 'batch-databricks':
      case 'batchassessment':
        return 'Batch Databricks Assessment';
      case 'capex':
      case 'projectevaluationcapex':
        return 'CapEx Evaluation';
      case 'rnd':
      case 'projectevaluationrnd':
        return 'R&D Evaluation';
      default:
        return jobType;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      case 'processing':
        return <Badge variant="info">Processing</Badge>;
      case 'completed':
        return <Badge variant="success">Completed</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const isJobStuck = (job: ActiveJob) => {
    if (!job.status) return false;
    const status = job.status.status.toLowerCase();
    return status === 'pending' || status === 'processing';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Batch Process</CardTitle>
          <p className="text-sm text-muted-foreground">
            Logged in as: {user?.emailAddress || ''}
          </p>
        </CardHeader>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="run">Run Batch</TabsTrigger>
          <TabsTrigger value="failed">Failed Jobs</TabsTrigger>
        </TabsList>

        <TabsContent value="run" className="space-y-6">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Fiscal Year Quarter</label>
                  <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a quarter" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableQuarters.map((q) => {
                        const value = `${formatFiscalYear(q.fiscalYear)} ${q.quarter}`;
                        return (
                          <SelectItem key={value} value={value}>
                            {value}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Area Level 1</label>
                  <Input
                    value={selectedAreaLevel}
                    onChange={(e) => setSelectedAreaLevel(e.target.value)}
                    placeholder="Enter area level 1 (leave empty for all)"
                    disabled={!selectedQuarter}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                <Button
                  onClick={() => handleTriggerJob('batch-databricks')}
                  disabled={!selectedQuarter || hasRunningJob || triggerLoading !== null}
                >
                  {triggerLoading === 'batch-databricks' ? (
                    <><Spinner className="size-4 mr-2" /> Running...</>
                  ) : (
                    'Run Batch Databricks Assessment'
                  )}
                </Button>
                <Button
                  onClick={() => handleTriggerJob('capex')}
                  disabled={!selectedQuarter || hasRunningJob || triggerLoading !== null}
                  variant="secondary"
                >
                  {triggerLoading === 'capex' ? (
                    <><Spinner className="size-4 mr-2" /> Running...</>
                  ) : (
                    'Run CapEx Evaluation'
                  )}
                </Button>
                <Button
                  onClick={() => handleTriggerJob('rnd')}
                  disabled={!selectedQuarter || hasRunningJob || triggerLoading !== null}
                  variant="secondary"
                >
                  {triggerLoading === 'rnd' ? (
                    <><Spinner className="size-4 mr-2" /> Running...</>
                  ) : (
                    'Run R&D Evaluation'
                  )}
                </Button>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-5 w-5" />
                  <div>
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </div>
                </Alert>
              )}
            </CardContent>
          </Card>

          {activeJobs.map((job) => (
            <Card key={job.jobId}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{getJobTypeLabel(job.jobType)}</CardTitle>
                  <div className="flex items-center gap-2">
                    {job.status ? getStatusBadge(job.status.status) : <Badge variant="warning">Starting...</Badge>}
                    {isJobStuck(job) && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleForceFailJob(job)}
                        disabled={forceFailLoading !== null}
                      >
                        {forceFailLoading === job.jobId ? (
                          <><Spinner className="size-4 mr-1" /> Failing...</>
                        ) : (
                          <><XCircle className="h-4 w-4 mr-1" /> Force Fail</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Job ID: {job.jobId}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {job.status ? (
                  <>
                    <div className="text-sm space-y-1">
                      <p>
                        Progress: {job.status.succeeded} / {job.status.total_items} items processed
                      </p>
                      {job.status.failed > 0 && (
                        <p className="text-destructive">Failed: {job.status.failed}</p>
                      )}
                      {job.status.fiscal_year_quarter && (
                        <p className="text-muted-foreground">Quarter: {job.status.fiscal_year_quarter}</p>
                      )}
                    </div>

                    {job.status.error_message && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-5 w-5" />
                        <div>
                          <AlertTitle>Error</AlertTitle>
                          <AlertDescription>{job.status.error_message}</AlertDescription>
                        </div>
                      </Alert>
                    )}

                    {job.status.logs.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium">Logs</p>
                        <div className="max-h-48 overflow-y-auto rounded border p-2 text-xs font-mono space-y-0.5">
                          {[...job.status.logs]
                            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                            .map((log, i) => (
                              <div key={`${job.jobId}-log-${i}`} className="flex gap-2">
                                <span className="text-muted-foreground whitespace-nowrap">
                                  {formatDateTimeUK(log.timestamp)}
                                </span>
                                <span>{log.message}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {job.polling && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Spinner className="size-3" />
                        <span>Polling for updates every 60 seconds...</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <Spinner className="size-4" />
                    <span className="text-sm">Fetching job status...</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="failed" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Failed & Incomplete Jobs</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchFailedJobs}
                  disabled={failedJobsLoading}
                >
                  {failedJobsLoading ? (
                    <Spinner className="size-4" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  <span className="ml-2">Refresh</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {failedJobsError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-5 w-5" />
                  <div>
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{failedJobsError}</AlertDescription>
                  </div>
                </Alert>
              )}

              {failedJobsLoading && failedJobs.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner className="size-6" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading failed jobs...</span>
                </div>
              ) : failedJobs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No failed or incomplete jobs found.
                </p>
              ) : (
                <div className="space-y-4">
                  {failedJobs.map((job) => (
                    <Card key={job.job_id} className="border">
                      <CardContent className="pt-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{getJobTypeLabel(job.job_type ?? '')}</span>
                            {getStatusBadge(job.status)}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRemoveJob(job)}
                              disabled={continueLoading !== null || removeLoading !== null}
                            >
                              {removeLoading === job.job_id ? (
                                <><Spinner className="size-4 mr-2" /> Removing...</>
                              ) : (
                                <><Trash2 className="h-4 w-4 mr-1" /> Remove</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleContinueJob(job)}
                              disabled={continueLoading !== null || removeLoading !== null}
                            >
                              {continueLoading === job.job_id ? (
                                <><Spinner className="size-4 mr-2" /> Continuing...</>
                              ) : (
                                'Continue'
                              )}
                            </Button>
                          </div>
                        </div>

                        <div className="text-xs text-muted-foreground">
                          Job ID: {job.job_id.substring(0, 8)}...
                        </div>

                        <div className="text-sm space-y-1">
                          {job.fiscal_year_quarter && (
                            <p>FY Quarter: {job.fiscal_year_quarter}</p>
                          )}
                          {job.area_level_1 && (
                            <p>Area Level 1: {job.area_level_1}</p>
                          )}
                          <p>
                            Progress: {job.succeeded} / {job.total_items} items processed
                            {job.failed > 0 && (
                              <span className="text-destructive ml-1">({job.failed} failed)</span>
                            )}
                          </p>
                        </div>

                        {job.error_message && (
                          <Alert variant="destructive">
                            <AlertCircle className="h-5 w-5" />
                            <div>
                              <AlertTitle>Error</AlertTitle>
                              <AlertDescription>{job.error_message}</AlertDescription>
                            </div>
                          </Alert>
                        )}

                        <div className="text-xs text-muted-foreground space-y-0.5">
                          <p>Submitted: {formatDateTimeUK(job.submitted_at)}</p>
                          {job.started_at && (
                            <p>Started: {formatDateTimeUK(job.started_at)}</p>
                          )}
                          {job.completed_at && (
                            <p>Completed: {formatDateTimeUK(job.completed_at)}</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
